import { ref, reactive, computed, onMounted, nextTick, onUnmounted, resolveComponent, openBlock, createElementBlock, normalizeClass, createCommentVNode, createElementVNode, normalizeStyle, renderSlot, Fragment, renderList, createBlock, toDisplayString, createVNode } from 'vue';
import propTypes from './utils/prop-types';
import { rgbTransform } from './utils/base';
import { addPxUnit } from './utils/format';
import { createComponent } from './utils/create-component';
import Icon from './icon';

function ownKeys(object, enumerableOnly) {
  var keys = Object.keys(object);
  if (Object.getOwnPropertySymbols) {
    var symbols = Object.getOwnPropertySymbols(object);
    enumerableOnly && (symbols = symbols.filter(function (sym) {
      return Object.getOwnPropertyDescriptor(object, sym).enumerable;
    })), keys.push.apply(keys, symbols);
  }
  return keys;
}
function _objectSpread2(target) {
  for (var i = 1; i < arguments.length; i++) {
    var source = null != arguments[i] ? arguments[i] : {};
    i % 2 ? ownKeys(Object(source), !0).forEach(function (key) {
      _defineProperty(target, key, source[key]);
    }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) {
      Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key));
    });
  }
  return target;
}
function _defineProperty(obj, key, value) {
  if (key in obj) {
    Object.defineProperty(obj, key, {
      value: value,
      enumerable: true,
      configurable: true,
      writable: true
    });
  } else {
    obj[key] = value;
  }
  return obj;
}

var props = {
  /**
   * 是否显示默认的返回按钮，主要用于没有左侧按钮的页面
   */
  hideBack: propTypes["boolean"]().def(false),
  /**
   * 左侧按钮，最大配置两个
   */
  leftBtns: propTypes.array(),
  /**
   * 右侧按钮，最大配置两个
   */
  rightBtns: propTypes.array(),
  /**
   * 标题区域是否居中
   */
  centerTitle: propTypes["boolean"]().def(true),
  /**
   * 自定义class覆盖样式
   */
  navBarClass: propTypes.string(),
  /**
   * 是否隐藏状态栏区域（常见于半屏或非沉浸式场景）
   */
  hideStatusBar: propTypes["boolean"]().def(false),
  /**
   * 状态栏高度
   */
  statusBarHeight: propTypes.number().def(0),
  /**
   * 自定义返回动作
   */
  onBack: propTypes["function"](),
  /**
   * 是否固定到顶部
   */
  fixed: propTypes["boolean"]().def(false),
  /**
   * 固定元素
   */
  fixedElement: propTypes.dom(),
  /**
   * 固定到顶部时，是否在标签位置生成一个等高的占位元素
   */
  placeholder: propTypes["boolean"]().def(false),
  /**
   * 层级
   */
  zIndex: propTypes.oneOfType([propTypes.string(), propTypes.number()]),
  /**
   * 背景样式
   */
  navBarStyle: {
    type: Object,
    "default": function _default() {
      return {
        color: '#555555',
        opacity: 1,
        backgroundColor: '#f6f6f6'
      };
    }
  },
  /**
   * 动画
   */
  animation: {
    type: Object,
    "default": function _default() {
      return {
        type: 'none',
        startDistance: 0,
        endDistance: 0
      };
    }
  }
};

var _createComponent = createComponent('nav-bar'),
  create = _createComponent.create,
  className = _createComponent.className;
var script = create({
  props: props,
  components: _defineProperty({}, Icon.name, Icon),
  setup: function setup(props) {
    var headerClassName = "".concat(className, "__header");
    var registeredListeners = [];
    var navHeight = ref();
    var isMounted = ref(false);
    var navBarWrap = ref();
    var navbarContainerRef = ref();
    var animationStyle = reactive({
      color: '',
      opacity: 0,
      borderColor: '',
      backgroundColor: '',
      borderBottom: ''
    });
    var navbarContainerStyle = computed(function () {
      var style = {};
      if (props.zIndex) {
        style.zIndex = props.zIndex;
      }
      if (props.fixed) {
        style.position = props.fixedElement ? 'absolute' : 'fixed';
        style.top = 0;
        style.left = 0;
        style.right = 0;
      }
      return _objectSpread2({}, style);
    });
    function getCustomBtnStyle(_ref) {
      var color = _ref.color,
        size = _ref.size;
      return {
        color: color,
        fontSize: size && addPxUnit(size)
      };
    }
    function handleBack() {
      if (props.onBack) {
        props.onBack();
      } else {
        history.back();
      }
    }
    function setanimationStyle() {
      // 基础样式复制
      animationStyle.color = props.navBarStyle.color || '';
      animationStyle.opacity = props.navBarStyle.opacity || 1;
      animationStyle.backgroundColor = props.navBarStyle.backgroundColor || '';
      // 当提供底边颜色时显示底边
      if (props.navBarStyle.borderColor) {
        animationStyle.borderBottom = '1px solid ' + (props.navBarStyle.borderColor || '#ededed');
      }
    }
    function setListener(fadeInHandler) {
      var _props$fixedElement;
      (_props$fixedElement = props.fixedElement) === null || _props$fixedElement === void 0 ? void 0 : _props$fixedElement.addEventListener('scroll', fadeInHandler);
      window.addEventListener('scroll', fadeInHandler);
      registeredListeners.push(function () {
        window.removeEventListener('scroll', fadeInHandler);
      });
    }
    function fadeIn() {
      var fixedElement = props.fixedElement,
        animation = props.animation;
      // 整体淡入动画
      animationStyle.opacity = 0;
      var start = animation.startDistance || 0;
      var end = animation.endDistance || Math.floor(window.screen.height / 3);
      var distance = end - start;
      var fadeInHandler = function fadeInHandler() {
        var scrolled = fixedElement ? fixedElement.scrollTop : document.body.scrollTop || document.documentElement.scrollTop;
        if (scrolled < start) {
          animationStyle.opacity = 0;
          return;
        }
        if (scrolled > end) {
          animationStyle.opacity = 1;
          return;
        }
        animationStyle.opacity = (scrolled - start) / distance;
      };
      setListener(fadeInHandler);
    }
    function fadeInBack() {
      // 淡入背景动画
      var fontColor = rgbTransform(animationStyle.color);
      var borderColor = rgbTransform(animationStyle.borderColor);
      var backgroundColor = rgbTransform(animationStyle.backgroundColor);
      var start = props.animation.startDistance || 0;
      var end = props.animation.endDistance || Math.floor(window.screen.height / 3);
      var distance = end - start;
      animationStyle.color = 'transparent';
      animationStyle.borderColor = "rgba(".concat(borderColor, ", 0)");
      animationStyle.backgroundColor = "rgba(".concat(backgroundColor, ", 0)");
      var fadeInBackHandler = function fadeInBackHandler() {
        var scrolled = props.fixedElement ? props.fixedElement.scrollTop : document.body.scrollTop || document.documentElement.scrollTop;
        if (scrolled < start) {
          animationStyle.color = 'transparent';
          animationStyle.borderColor = 'transparent';
          animationStyle.backgroundColor = 'transparent';
          return;
        }
        if (scrolled > end) {
          animationStyle.color = "rgba(".concat(fontColor, ", 1)");
          animationStyle.borderColor = "rgba(".concat(borderColor, ", 1)");
          animationStyle.backgroundColor = "rgba(".concat(backgroundColor, ", 1)");
          return;
        }
        animationStyle.color = "rgba(".concat(fontColor, ", ").concat((scrolled - start) / distance, ")");
        animationStyle.borderColor = "rgba(".concat(borderColor, ", ").concat((scrolled - start) / distance, ")");
        animationStyle.backgroundColor = "rgba(".concat(backgroundColor, ", ").concat((scrolled - start) / distance, ")");
      };
      setListener(fadeInBackHandler);
    }
    function fadeInTitle() {
      // 淡入标题动画
      var fontColor = rgbTransform(animationStyle.color);
      var start = props.animation.startDistance || 0;
      var end = props.animation.endDistance || Math.floor(window.screen.height / 3);
      var distance = end - start;
      animationStyle.color = 'transparent';
      var fadeInTitleHandler = function fadeInTitleHandler() {
        var scrolled = props.fixedElement ? props.fixedElement.scrollTop : document.body.scrollTop || document.documentElement.scrollTop;
        if (scrolled < start) {
          animationStyle.color = 'transparent';
          return;
        }
        if (scrolled > end) {
          animationStyle.color = "rgba(".concat(fontColor, ", 1)");
          return;
        }
        animationStyle.color = "rgba(".concat(fontColor, ", ").concat((scrolled - start) / distance, ")");
      };
      setListener(fadeInTitleHandler);
    }
    onMounted(function () {
      var _props$fixedElement2;
      (_props$fixedElement2 = props.fixedElement) === null || _props$fixedElement2 === void 0 ? void 0 : _props$fixedElement2.appendChild(navbarContainerRef.value);
      isMounted.value = true;
      // 有可能mounted之后才能获取到container
      nextTick(function () {
        if (props.fixed && props.placeholder) {
          var _navbarContainerRef$v;
          navHeight.value = navbarContainerRef === null || navbarContainerRef === void 0 ? void 0 : (_navbarContainerRef$v = navbarContainerRef.value) === null || _navbarContainerRef$v === void 0 ? void 0 : _navbarContainerRef$v.getBoundingClientRect().height;
          navBarWrap.value.style.height = navHeight.value + 'px';
        }
        setanimationStyle();
        // 区分动画
        switch (props.animation.type) {
          case 'none':
            break;
          case 'fadeIn':
            fadeIn();
            break;
          case 'fadeInBack':
            fadeInBack();
            break;
          case 'fadeInTitle':
            fadeInTitle();
            break;
        }
      });
    });
    onUnmounted(function () {
      registeredListeners.forEach(function (clearEvent) {
        return clearEvent();
      });
    });
    return {
      headerClassName: headerClassName,
      className: className,
      navBarWrap: navBarWrap,
      navbarContainerStyle: navbarContainerStyle,
      navbarContainerRef: navbarContainerRef,
      animationStyle: animationStyle,
      getCustomBtnStyle: getCustomBtnStyle,
      handleBack: handleBack
    };
  }
});

var _hoisted_1 = ["onClick"];
var _hoisted_2 = ["onClick"];
function render(_ctx, _cache, $props, $setup, $data, $options) {
  var _component_sp_icon = resolveComponent("sp-icon");
  return openBlock(), createElementBlock("div", {
    "class": normalizeClass([_ctx.className])
  }, [_ctx.fixed && _ctx.placeholder ? (openBlock(), createElementBlock("div", {
    key: 0,
    "class": normalizeClass("".concat(_ctx.className, "__placeholder")),
    ref: "navBarWrap"
  }, null, 2 /* CLASS */)) : createCommentVNode("v-if", true), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.className, "__container")),
    style: normalizeStyle([_ctx.animationStyle, _ctx.navbarContainerStyle]),
    ref: "navbarContainerRef"
  }, [!_ctx.hideStatusBar ? (openBlock(), createElementBlock("div", {
    key: 0,
    "class": normalizeClass("".concat(_ctx.className, "__status_bar")),
    style: normalizeStyle({
      height: "".concat(_ctx.statusBarHeight, "px")
    })
  }, null, 6 /* CLASS, STYLE */)) : createCommentVNode("v-if", true), createElementVNode("div", {
    "class": normalizeClass(_ctx.headerClassName)
  }, [createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.headerClassName, "_title"))
  }, [_ctx.centerTitle ? renderSlot(_ctx.$slots, "default", {
    key: 0
  }) : createCommentVNode("v-if", true)], 2 /* CLASS */), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.headerClassName, "_cover")),
    style: normalizeStyle([{
      opacity: _ctx.animationStyle.opacity
    }])
  }, [createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.headerClassName, "_cover_left"))
  }, [renderSlot(_ctx.$slots, "left", {}, function () {
    return [_ctx.leftBtns ? (openBlock(true), createElementBlock(Fragment, {
      key: 0
    }, renderList(_ctx.leftBtns, function (item, index) {
      return openBlock(), createElementBlock("div", {
        "class": normalizeClass("".concat(_ctx.headerClassName, "_btn")),
        key: index,
        onClick: function onClick() {
          item.onClick && item.onClick();
        }
      }, [item.icon ? (openBlock(), createBlock(_component_sp_icon, {
        key: 0,
        "class": normalizeClass("".concat(_ctx.headerClassName, "_icon")),
        name: item.icon,
        style: normalizeStyle(_ctx.getCustomBtnStyle(item))
      }, null, 8 /* PROPS */, ["class", "name", "style"])) : (openBlock(), createElementBlock("div", {
        key: 1,
        "class": normalizeClass("".concat(_ctx.headerClassName, "_text")),
        style: normalizeStyle(_ctx.getCustomBtnStyle(item))
      }, toDisplayString(item.text), 7 /* TEXT, CLASS, STYLE */))], 10 /* CLASS, PROPS */, _hoisted_1);
    }), 128 /* KEYED_FRAGMENT */)) : !_ctx.hideBack ? (openBlock(), createElementBlock("div", {
      key: 1,
      "class": normalizeClass(["".concat(_ctx.headerClassName, "_btn"), "".concat(_ctx.headerClassName, "_btn_back")]),
      onClick: _cache[0] || (_cache[0] = function () {
        return _ctx.handleBack && _ctx.handleBack.apply(_ctx, arguments);
      })
    }, [createVNode(_component_sp_icon, {
      "class": normalizeClass("".concat(_ctx.headerClassName, "_icon")),
      name: "base_back"
    }, null, 8 /* PROPS */, ["class"])], 2 /* CLASS */)) : createCommentVNode("v-if", true)];
  })], 2 /* CLASS */), !_ctx.centerTitle ? (openBlock(), createElementBlock("div", {
    key: 0,
    "class": normalizeClass("".concat(_ctx.headerClassName, "_title_inner"))
  }, [renderSlot(_ctx.$slots, "default")], 2 /* CLASS */)) : createCommentVNode("v-if", true), createElementVNode("div", {
    "class": normalizeClass("".concat(_ctx.headerClassName, "_cover_right"))
  }, [renderSlot(_ctx.$slots, "right", {}, function () {
    return [_ctx.rightBtns ? (openBlock(true), createElementBlock(Fragment, {
      key: 0
    }, renderList(_ctx.rightBtns, function (item, index) {
      return openBlock(), createElementBlock("div", {
        "class": normalizeClass("".concat(_ctx.headerClassName, "_btn")),
        key: index,
        onClick: function onClick() {
          item.onClick && item.onClick();
        }
      }, [item.icon ? (openBlock(), createBlock(_component_sp_icon, {
        key: 0,
        "class": normalizeClass("".concat(_ctx.headerClassName, "_icon")),
        name: item.icon,
        style: normalizeStyle(_ctx.getCustomBtnStyle(item))
      }, null, 8 /* PROPS */, ["class", "name", "style"])) : (openBlock(), createElementBlock("div", {
        key: 1,
        "class": normalizeClass("".concat(_ctx.headerClassName, "_text")),
        style: normalizeStyle(_ctx.getCustomBtnStyle(item))
      }, toDisplayString(item.text), 7 /* TEXT, CLASS, STYLE */))], 10 /* CLASS, PROPS */, _hoisted_2);
    }), 128 /* KEYED_FRAGMENT */)) : createCommentVNode("v-if", true)];
  })], 2 /* CLASS */)], 6 /* CLASS, STYLE */)], 2 /* CLASS */)], 6 /* CLASS, STYLE */)], 2 /* CLASS */);
}

script.render = render;
script.__file = "components/src/component/nav-bar/nav-bar.vue";

var _NavBar = script;

export { _NavBar as default };
